import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { ConfirmDialog } from "./components/ConfirmDialog";
import { ContextBar, type EffectKey } from "./components/ContextBar";
import { EditorCanvas, type EditorCanvasHandle, type SelectMode } from "./components/EditorCanvas";
import { EmptyState } from "./components/EmptyState";
import { ExportDialog, type ExportScope } from "./components/ExportDialog";
import { Filmstrip } from "./components/Filmstrip";
import { Icon } from "./components/Icon";
import { RestorePanel } from "./components/RestorePanel";
import { ShortcutsDialog } from "./components/ShortcutsDialog";
import { ToolRail } from "./components/ToolRail";
import { TopBar } from "./components/TopBar";
import { clampZoom } from "./components/canvas/useViewport";
import { copyRegion, editableRegions, mapRegions, mixedEffectKeys } from "./editor/regions";
import {
  PEN_WIDTH_RANGE,
  type Coverage,
  type EditableRegion,
  type EditorImage,
  type MosaicRegion,
  type RegionEffect,
  type ShapeKind,
  type ToolMode,
} from "./editor/types";
import { useBackgroundRemovalProcessor } from "./hooks/useBackgroundRemovalProcessor";
import { useEffectPreview } from "./hooks/useEffectPreview";
import { useImageStore } from "./hooks/useImageStore";
import { useMosaicProcessor } from "./hooks/useMosaicProcessor";
import { usePersistentSettings } from "./hooks/usePersistentSettings";
import { useRestoration } from "./hooks/useRestoration";
import { filesFromDrop } from "./utils/drop";
import {
  composeSourceImage,
  downloadBlob,
  fileToEditorImage,
  imageDataToExportBlob,
  isSupportedImage,
  loadDemoImage,
  outputName,
  zipBlobs,
} from "./utils/files";

/** Room at the top of the canvas for the floating context bar. */
const CONTEXT_BAR_INSET = 64;
const ZOOM_STEP = 1.25;

type Dialog =
  | { kind: "export" }
  | { kind: "shortcuts" }
  | { kind: "home" }
  | { kind: "discard" }
  | { kind: "remove"; index: number };

const TOOL_KEYS: Record<string, { tool: ToolMode; shapeKind?: ShapeKind }> = {
  KeyV: { tool: "select" },
  KeyB: { tool: "pen" },
  KeyR: { tool: "shape", shapeKind: "rect" },
  KeyO: { tool: "shape", shapeKind: "ellipse" },
  KeyL: { tool: "polygon" },
  KeyE: { tool: "erase" },
  KeyA: { tool: "restore" },
};

function batchEntryName(image: EditorImage, format: Parameters<typeof outputName>[1]): string {
  const parts = image.relativePath?.split("/").filter(Boolean) ?? [];
  if (parts.length <= 1) return outputName(image.name, format);
  parts[0] = `${parts[0]}_mc`;
  parts[parts.length - 1] = outputName(parts[parts.length - 1], format);
  return parts.join("/");
}

/** Only drags that carry files from outside the page, not filmstrip reordering. */
function hasFiles(transfer: DataTransfer | null): boolean {
  return Boolean(transfer && Array.from(transfer.types).includes("Files"));
}

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    (target instanceof HTMLInputElement &&
      !["checkbox", "radio", "range", "button"].includes(target.type)) ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

function hasEdits(image: EditorImage): boolean {
  return image.coverage !== "inside" || image.regions.length > 0 || image.restorations.length > 0;
}

export default function App() {
  const store = useImageStore();
  const { images, activeImage, activeIndex } = store;
  const [settings, updateSettings] = usePersistentSettings();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [zoom, setZoomState] = useState(1);
  const [fitRequest, setFitRequest] = useState(0);
  const [draft, setDraft] = useState<MosaicRegion | null>(null);
  const [comparing, setComparing] = useState(false);
  const [brushPreview, setBrushPreview] = useState(false);
  const [polygonVertices, setPolygonVertices] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dropActive, setDropActive] = useState(false);
  const [toast, setToast] = useState("");
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [exportScope, setExportScope] = useState<ExportScope>("current");
  const [exportProgress, setExportProgress] = useState("");
  const canvasRef = useRef<EditorCanvasHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const clipboardRef = useRef<{ imageId: string; regions: EditableRegion[] } | null>(null);
  const editingRef = useRef(false);
  const exportMosaic = useMosaicProcessor();
  const removeBackground = useBackgroundRemovalProcessor();
  const preview = useEffectPreview(activeImage, draft, setToast);

  const showToast = useCallback((message: string) => setToast(message), []);
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const restoration = useRestoration(
    activeImage,
    useCallback(
      (patch) => {
        store.updateActive((image) => ({
          ...image,
          restorations: [...image.restorations, patch],
          dirty: true,
          saved: false,
        }));
        showToast("AIによる復元結果を適用しました");
      },
      [showToast, store],
    ),
  );

  const regions = useMemo(
    () => (activeImage ? editableRegions(activeImage.regions) : []),
    [activeImage],
  );
  const selectedRegions = useMemo(() => {
    const ids = new Set(selectedIds);
    return regions.filter((region) => ids.has(region.id));
  }, [regions, selectedIds]);
  const hasSelection = settings.tool === "select" && selectedRegions.length > 0;

  /* ---- image switching ------------------------------------------------- */

  const [shownImageId, setShownImageId] = useState(activeImage?.id);
  if (shownImageId !== activeImage?.id) {
    setShownImageId(activeImage?.id);
    setSelectedIds([]);
    setZoomState(1);
    setDraft(null);
  }
  const cancelRestoration = restoration.cancel;
  useEffect(() => {
    cancelRestoration();
  }, [activeImage?.id, cancelRestoration]);

  // Drop selections that no longer exist (undo, delete, other image).
  useEffect(() => {
    setSelectedIds((current) => {
      const next = current.filter((id) => regions.some((region) => region.id === id));
      return next.length === current.length ? current : next;
    });
  }, [regions]);

  useEffect(() => {
    if (!images.some((image) => image.dirty)) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [images]);

  /* ---- edits ------------------------------------------------------------ */

  /**
   * Continuous edits (slider drags, transforms) take one history checkpoint
   * at the start so the whole gesture undoes in a single step.
   */
  const beginEdit = useCallback(() => {
    if (editingRef.current) return;
    store.checkpoint();
    editingRef.current = true;
  }, [store]);
  const endEdit = useCallback(() => {
    editingRef.current = false;
  }, []);

  const editActive = useCallback(
    (updater: (image: EditorImage) => EditorImage) => {
      store.updateActive(
        (image) => ({ ...updater(image), dirty: true, saved: false }),
        { history: !editingRef.current },
      );
    },
    [store],
  );

  const setTool = useCallback(
    (tool: ToolMode, shapeKind?: ShapeKind) => {
      if (tool !== "restore") restoration.cancel();
      if (tool !== "select") setSelectedIds([]);
      updateSettings(shapeKind ? { tool, shapeKind } : { tool });
    },
    [restoration, updateSettings],
  );

  const addRegion = useCallback(
    (region: MosaicRegion) => {
      editActive((image) => ({ ...image, regions: [...image.regions, region] }));
      // Shapes hand over to selection so their handles appear right away;
      // brushes stay active for the next stroke, as in Canva's draw tool.
      if (region.kind === "rect" || region.kind === "ellipse" || region.kind === "triangle" || region.kind === "polygon") {
        setSelectedIds([region.id]);
        updateSettings({ tool: "select" });
      }
    },
    [editActive, updateSettings],
  );

  const updateRegions = useCallback(
    (next: EditableRegion[], final: boolean) => {
      const byId = new Map(next.map((region) => [region.id, region]));
      editActive((image) => ({
        ...image,
        regions: mapRegions(image.regions, new Set(byId.keys()), (region) => byId.get(region.id) ?? region),
      }));
      if (final) endEdit();
    },
    [editActive, endEdit],
  );

  const updateSelected = useCallback(
    (update: (region: EditableRegion) => EditableRegion) => {
      if (selectedIds.length === 0) return;
      editActive((image) => ({
        ...image,
        regions: mapRegions(image.regions, new Set(selectedIds), update),
      }));
    },
    [editActive, selectedIds],
  );

  const changeEffect = useCallback(
    (patch: Partial<RegionEffect>) => {
      if (hasSelection) {
        updateSelected((region) => ({ ...region, effect: { ...region.effect, ...patch } }));
      }
      // The next range starts with whatever was chosen last.
      updateSettings((current) => ({ effect: { ...current.effect, ...patch } }));
    },
    [hasSelection, updateSelected, updateSettings],
  );

  const changePenWidth = useCallback(
    (penWidth: number) => {
      if (hasSelection) {
        updateSelected((region) => (region.kind === "stroke" ? { ...region, penWidth } : region));
      }
      updateSettings({ penWidth });
    },
    [hasSelection, updateSelected, updateSettings],
  );

  const changeCoverage = useCallback(
    (coverage: Coverage) => editActive((image) => ({ ...image, coverage })),
    [editActive],
  );

  const changeCoverageEffect = useCallback(
    (patch: Partial<RegionEffect>) =>
      editActive((image) => ({
        ...image,
        coverageEffect: { ...image.coverageEffect, ...patch },
      })),
    [editActive],
  );

  const applyCoverageToAll = useCallback(() => {
    if (!activeImage) return;
    const { coverage, coverageEffect } = activeImage;
    store.updateAll((image) =>
      image.coverage === coverage && image.coverageEffect === coverageEffect
        ? image
        : { ...image, coverage, coverageEffect, dirty: true, saved: false },
    );
    showToast(`${images.length}枚すべてに適用しました`);
  }, [activeImage, images.length, showToast, store]);

  const deleteSelected = useCallback(() => {
    if (selectedIds.length === 0) return;
    const ids = new Set(selectedIds);
    editActive((image) => ({
      ...image,
      regions: image.regions.filter((region) => !ids.has(region.id)),
    }));
    setSelectedIds([]);
  }, [editActive, selectedIds]);

  const pasteRegions = useCallback(
    (source: EditableRegion[], offset: number) => {
      if (source.length === 0) return;
      const copies = source.map((region) => copyRegion(region, offset));
      editActive((image) => ({ ...image, regions: [...image.regions, ...copies] }));
      setSelectedIds(copies.map(({ id }) => id));
      updateSettings({ tool: "select" });
    },
    [editActive, updateSettings],
  );

  const duplicateSelected = useCallback(
    () => pasteRegions(selectedRegions, 16),
    [pasteRegions, selectedRegions],
  );

  const copySelected = useCallback(() => {
    if (!activeImage || selectedRegions.length === 0) return;
    clipboardRef.current = { imageId: activeImage.id, regions: selectedRegions };
    showToast(`${selectedRegions.length}件の範囲をコピーしました`);
  }, [activeImage, selectedRegions, showToast]);

  const pasteClipboard = useCallback(() => {
    const clipboard = clipboardRef.current;
    if (!clipboard || !activeImage) return;
    // On another image, paste in place so the same spot is covered on every
    // screenshot of a series.
    pasteRegions(clipboard.regions, clipboard.imageId === activeImage.id ? 16 : 0);
  }, [activeImage, pasteRegions]);

  /** Holding an arrow key moves continuously but undoes as one step. */
  const nudgeSelected = useCallback(
    (dx: number, dy: number, repeat: boolean) => {
      const ids = new Set(selectedIds);
      store.updateActive(
        (image) => ({
          ...image,
          regions: mapRegions(image.regions, ids, (region) => ({
            ...region,
            x: region.x + dx,
            y: region.y + dy,
          })),
          dirty: true,
          saved: false,
        }),
        { history: !repeat },
      );
    },
    [selectedIds, store],
  );

  const select = useCallback(
    (ids: string[], mode: SelectMode) => {
      setSelectedIds((current) => {
        if (mode === "replace") return ids;
        if (mode === "add") return [...new Set([...current, ...ids])];
        const toggled = new Set(current);
        ids.forEach((id) => (toggled.has(id) ? toggled.delete(id) : toggled.add(id)));
        return [...toggled];
      });
    },
    [],
  );

  const setZoom = useCallback((value: number) => setZoomState(clampZoom(value)), []);
  const fit = useCallback(() => {
    setZoomState(1);
    setFitRequest((value) => value + 1);
  }, []);

  /* ---- loading ---------------------------------------------------------- */

  const addFiles = useCallback(
    async (entries: Array<{ file: File; path?: string }>, replace = false) => {
      const supported = entries.filter(({ file }) => isSupportedImage(file));
      if (supported.length === 0) {
        showToast("対応している画像が見つかりませんでした");
        return;
      }
      setLoading(true);
      try {
        const loaded: EditorImage[] = [];
        let failed = 0;
        for (const entry of supported) {
          try {
            const image = await fileToEditorImage(entry.file, entry.path);
            loaded.push({ ...image, coverageEffect: settings.effect });
          } catch (error) {
            failed += 1;
            if (supported.length === 1) throw error;
          }
        }
        store.add(loaded, replace);
        restoration.cancel();
        if (failed > 0) showToast(`${failed}件の画像を読み込めませんでした`);
      } catch (error) {
        showToast(error instanceof Error ? error.message : "画像を読み込めませんでした");
      } finally {
        setLoading(false);
      }
    },
    [restoration, settings.effect, showToast, store],
  );

  const loadDemo = useCallback(async () => {
    setLoading(true);
    try {
      const demo = await loadDemoImage();
      store.add([{ ...demo, coverageEffect: settings.effect }], true);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "サンプル画像を読み込めませんでした");
    } finally {
      setLoading(false);
    }
  }, [settings.effect, showToast, store]);

  const goHome = useCallback(() => {
    store.clear();
    clipboardRef.current = null;
    restoration.cancel();
    setDialog(null);
    updateSettings({ tool: "select", preview: false });
  }, [restoration, store, updateSettings]);

  const discardAll = useCallback(() => {
    store.updateAll((image) => ({
      ...image,
      coverage: "inside",
      regions: [],
      restorations: [],
      dirty: false,
      saved: false,
    }));
    store.resetHistory();
    setSelectedIds([]);
    restoration.cancel();
    setDialog(null);
    showToast("すべての加工を取り消しました");
  }, [restoration, showToast, store]);

  /* ---- export ----------------------------------------------------------- */

  const renderForExport = useCallback(
    async (image: EditorImage) => {
      const composed = await composeSourceImage(image);
      const source = settings.exportRemoveBackground ? await removeBackground(composed) : composed;
      const result = await exportMosaic(source, {
        regions: image.regions,
        coverage: image.coverage,
        coverageEffect: image.coverageEffect,
      });
      return imageDataToExportBlob(result, image, {
        stripMetadata: settings.stripMetadata,
        format: settings.exportFormat,
        scale: settings.exportScale,
        quality: settings.exportQuality,
      });
    },
    [
      exportMosaic,
      removeBackground,
      settings.exportFormat,
      settings.exportQuality,
      settings.exportRemoveBackground,
      settings.exportScale,
      settings.stripMetadata,
    ],
  );

  const markSaved = useCallback(
    (ids: Set<string>) => {
      ids.forEach((id) =>
        store.updateImage(id, (image) => ({ ...image, dirty: false, saved: true }), {
          history: false,
        }),
      );
    },
    [store],
  );

  const exportImages = useCallback(async () => {
    if (!activeImage) return;
    const targets = exportScope === "all" ? images : [activeImage];
    setBusy(true);
    try {
      const files: Array<{ name: string; blob: Blob }> = [];
      for (const [index, image] of targets.entries()) {
        setExportProgress(`${index + 1} / ${targets.length} を処理中…`);
        files.push({
          name:
            targets.length > 1
              ? batchEntryName(image, settings.exportFormat)
              : outputName(image.name, settings.exportFormat),
          blob: await renderForExport(image),
        });
      }
      if (files.length === 1) downloadBlob(files[0].blob, files[0].name);
      else downloadBlob(await zipBlobs(files), "MosaicStudio_mc.zip");
      markSaved(new Set(targets.map(({ id }) => id)));
      setExportProgress("");
      setDialog(null);
      showToast(files.length === 1 ? "ダウンロードしました" : `${files.length}枚をZIPでダウンロードしました`);
    } catch (error) {
      setExportProgress(error instanceof Error ? error.message : "ダウンロードに失敗しました");
    } finally {
      setBusy(false);
    }
  }, [activeImage, exportScope, images, markSaved, renderForExport, settings.exportFormat, showToast]);

  const openExport = useCallback(() => {
    if (!activeImage) return;
    setExportScope(images.length > 1 ? "all" : "current");
    setExportProgress("");
    setDialog({ kind: "export" });
  }, [activeImage, images.length]);

  const navigateTo = useCallback(
    async (index: number) => {
      const target = Math.max(0, Math.min(images.length - 1, index));
      if (target === activeIndex || !images[target]) return;
      if (settings.autoDownload && activeImage?.dirty) {
        try {
          downloadBlob(await renderForExport(activeImage), outputName(activeImage.name, settings.exportFormat));
          markSaved(new Set([activeImage.id]));
        } catch (error) {
          showToast(error instanceof Error ? error.message : "自動ダウンロードに失敗しました");
          return;
        }
      }
      store.select(target);
    },
    [activeImage, activeIndex, images, markSaved, renderForExport, settings.autoDownload, settings.exportFormat, showToast, store],
  );

  const requestRemove = useCallback(
    (index: number) => {
      if (images[index]?.dirty) setDialog({ kind: "remove", index });
      else store.remove(index);
    },
    [images, store],
  );

  /* ---- keyboard & clipboard --------------------------------------------- */

  const handleKeyDown = (event: KeyboardEvent) => {
    if (dialog || isTypingTarget(event.target) || !activeImage) return;
    // A focused slider owns its arrow keys.
    if (
      event.target instanceof HTMLInputElement &&
      event.target.type === "range" &&
      /^(Arrow|Page|Home|End)/.test(event.key)
    ) {
      return;
    }
    const modifier = event.ctrlKey || event.metaKey;
    const key = event.key.toLowerCase();

    if (modifier) {
      const actions: Record<string, () => void> = {
        z: () => (event.shiftKey ? store.redo() : store.undo()),
        y: () => store.redo(),
        s: openExport,
        a: () => {
          setSelectedIds(regions.map(({ id }) => id));
          updateSettings({ tool: "select" });
        },
        c: copySelected,
        d: duplicateSelected,
        "=": () => setZoom(zoom * ZOOM_STEP),
        "+": () => setZoom(zoom * ZOOM_STEP),
        "-": () => setZoom(zoom / ZOOM_STEP),
        "0": fit,
      };
      const action = actions[key];
      if (action) {
        event.preventDefault();
        action();
      }
      return;
    }
    if (event.altKey) return;

    if (polygonVertices > 0) {
      const canvas = canvasRef.current;
      if (event.key === "Enter") canvas?.finishPolygon();
      else if (event.key === "Escape") canvas?.cancelPolygon();
      else if (event.key === "Backspace" || event.key === "Delete") canvas?.undoPolygonPoint();
      else return;
      event.preventDefault();
      return;
    }

    const arrows: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    if (arrows[event.key]) {
      event.preventDefault();
      const [dx, dy] = arrows[event.key];
      if (hasSelection) {
        const step = event.shiftKey ? 10 : 1;
        nudgeSelected(dx * step, dy * step, event.repeat);
      } else if (dy === 0 && !event.repeat) {
        void navigateTo(activeIndex + dx);
      }
      return;
    }

    if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      deleteSelected();
    } else if (event.key === "Escape") {
      if (selectedIds.length > 0) setSelectedIds([]);
      else if (settings.preview) updateSettings({ preview: false });
      else setTool("select");
    } else if (event.key === "[" || event.key === "]") {
      const step = Math.max(2, Math.round(settings.penWidth * 0.15));
      const next = settings.penWidth + (event.key === "]" ? step : -step);
      changePenWidth(Math.max(PEN_WIDTH_RANGE.min, Math.min(PEN_WIDTH_RANGE.max, next)));
    } else if (event.code === "KeyP") {
      updateSettings({ preview: !settings.preview });
    } else if (event.code === "KeyC") {
      setComparing(true);
    } else if (TOOL_KEYS[event.code] && !event.repeat) {
      const { tool, shapeKind } = TOOL_KEYS[event.code];
      setTool(tool, shapeKind);
    }
  };

  const keyboardRef = useRef(handleKeyDown);
  useLayoutEffect(() => {
    keyboardRef.current = handleKeyDown;
  });
  useEffect(() => {
    const down = (event: KeyboardEvent) => keyboardRef.current(event);
    const up = (event: KeyboardEvent) => {
      if (event.code === "KeyC") setComparing(false);
    };
    const blur = () => setComparing(false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", blur);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", blur);
    };
  }, []);

  const handlePaste = (event: ClipboardEvent) => {
    if (isTypingTarget(event.target) || dialog) return;
    const files = Array.from(event.clipboardData?.files ?? []).filter((file) =>
      file.type.startsWith("image/"),
    );
    if (files.length > 0) {
      event.preventDefault();
      // Screenshots arrive as "image.png"; give each a distinct, dated name.
      const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      void addFiles(
        files.map((file, index) => ({
          file: new File([file], `pasted-${stamp}${files.length > 1 ? `-${index + 1}` : ""}.${
            file.type.split("/")[1] === "jpeg" ? "jpg" : (file.type.split("/")[1] ?? "png")
          }`, { type: file.type }),
        })),
      );
    } else if (activeImage && clipboardRef.current) {
      event.preventDefault();
      pasteClipboard();
    }
  };
  const pasteRef = useRef(handlePaste);
  useLayoutEffect(() => {
    pasteRef.current = handlePaste;
  });
  useEffect(() => {
    const paste = (event: ClipboardEvent) => pasteRef.current(event);
    window.addEventListener("paste", paste);
    return () => window.removeEventListener("paste", paste);
  }, []);

  /* ---- render ----------------------------------------------------------- */

  const shownEffect = hasSelection ? selectedRegions[0].effect : settings.effect;
  const mixed: EffectKey[] = hasSelection ? mixedEffectKeys(selectedRegions) : [];
  const strokeWidths = selectedRegions.flatMap((region) =>
    region.kind === "stroke" ? [region.penWidth] : [],
  );
  const selectionPenWidth =
    hasSelection && strokeWidths.length === selectedRegions.length ? strokeWidths[0] : null;
  const contextBarVisible = !settings.preview && settings.tool !== "restore";
  const lossy =
    settings.exportFormat === "jpeg" ||
    settings.exportFormat === "webp" ||
    (settings.exportFormat === "original" &&
      (activeImage?.mime === "image/jpeg" || activeImage?.mime === "image/webp"));

  return (
    <div
      className={`app-shell ${dropActive ? "is-drop-active" : ""}`}
      onDragEnter={(event) => {
        if (!hasFiles(event.dataTransfer)) return;
        event.preventDefault();
        setDropActive(true);
      }}
      onDragOver={(event) => {
        if (hasFiles(event.dataTransfer)) event.preventDefault();
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropActive(false);
      }}
      onDrop={(event) => {
        setDropActive(false);
        if (!hasFiles(event.dataTransfer)) return;
        event.preventDefault();
        void filesFromDrop(event.dataTransfer).then((entries) => addFiles(entries));
      }}
    >
      <input
        ref={fileInputRef}
        className="file-input-hidden"
        type="file"
        aria-hidden="true"
        tabIndex={-1}
        accept=".png,.jpg,.jpeg,.webp,.bmp"
        multiple
        onChange={(event) => {
          const entries = Array.from(event.target.files ?? []).map((file) => ({ file }));
          event.target.value = "";
          void addFiles(entries);
        }}
      />
      <input
        ref={folderInputRef}
        className="file-input-hidden"
        type="file"
        aria-hidden="true"
        tabIndex={-1}
        multiple
        {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
        onChange={(event) => {
          const entries = Array.from(event.target.files ?? []).map((file) => ({
            file,
            path: file.webkitRelativePath || file.name,
          }));
          event.target.value = "";
          void addFiles(entries, true);
        }}
      />

      <TopBar
        fileName={activeImage?.name}
        imageCount={images.length}
        canUndo={store.canUndo}
        canRedo={store.canRedo}
        preview={settings.preview}
        busy={busy}
        hasEdits={images.some(hasEdits)}
        onHome={() => setDialog({ kind: "home" })}
        onAddImages={() => fileInputRef.current?.click()}
        onDiscardAll={() => setDialog({ kind: "discard" })}
        onShowShortcuts={() => setDialog({ kind: "shortcuts" })}
        onUndo={store.undo}
        onRedo={store.redo}
        onTogglePreview={() => updateSettings({ preview: !settings.preview })}
        onExport={openExport}
      />

      {activeImage ? (
        <div className={`editor-layout ${settings.tool === "restore" ? "has-panel" : ""}`}>
          <ToolRail tool={settings.tool} disabled={busy} onChange={(tool) => setTool(tool)} />
          {settings.tool === "restore" && (
            <RestorePanel
              restoration={restoration.state}
              onChange={restoration.change}
              onApply={restoration.apply}
              onRetry={restoration.retry}
              onCancel={restoration.cancel}
            />
          )}
          <div className="canvas-column">
            {contextBarVisible && (
              <ContextBar
                tool={settings.tool}
                shapeKind={settings.shapeKind}
                penWidth={settings.penWidth}
                effect={shownEffect}
                mixed={mixed}
                coverage={activeImage.coverage}
                coverageEffect={activeImage.coverageEffect}
                selectionCount={hasSelection ? selectedRegions.length : 0}
                selectionPenWidth={selectionPenWidth}
                imageCount={images.length}
                polygonVertices={polygonVertices}
                disabled={busy}
                onShapeKindChange={(shapeKind) => updateSettings({ shapeKind })}
                onPenWidthChange={changePenWidth}
                onEffectChange={changeEffect}
                onCoverageChange={changeCoverage}
                onCoverageEffectChange={changeCoverageEffect}
                onApplyToAll={applyCoverageToAll}
                onInteractionStart={beginEdit}
                onInteractionEnd={endEdit}
                onBrushPreview={setBrushPreview}
                onDuplicate={duplicateSelected}
                onDelete={deleteSelected}
                onPolygonFinish={() => canvasRef.current?.finishPolygon()}
                onPolygonUndo={() => canvasRef.current?.undoPolygonPoint()}
                onPolygonCancel={() => canvasRef.current?.cancelPolygon()}
              />
            )}
            {settings.preview && (
              <button
                className="preview-exit"
                type="button"
                onClick={() => updateSettings({ preview: false })}
              >
                <Icon name="close" size={15} />
                プレビューを終了
              </button>
            )}
            <EditorCanvas
              ref={canvasRef}
              image={activeImage}
              rendered={preview.bitmap}
              revision={preview.revision}
              tool={settings.tool}
              shapeKind={settings.shapeKind}
              penWidth={settings.penWidth}
              effect={settings.effect}
              showOutlines={settings.showOutlines}
              showGrid={settings.showGrid}
              gridSize={shownEffect.blockSize}
              preview={settings.preview}
              comparing={comparing}
              selectedIds={hasSelection ? selectedIds : []}
              zoom={zoom}
              fitRequest={fitRequest}
              brushPreview={brushPreview}
              restorePreview={restoration.preview}
              topInset={contextBarVisible ? CONTEXT_BAR_INSET : 0}
              onZoomChange={setZoom}
              onSelect={select}
              onAddRegion={addRegion}
              onDraftChange={setDraft}
              onBeginEdit={beginEdit}
              onUpdateRegions={updateRegions}
              onRequestRestore={(rect) => void restoration.run(rect)}
              onPolygonChange={setPolygonVertices}
            />
          </div>
          <Filmstrip
            images={images}
            activeIndex={activeIndex}
            zoom={zoom}
            showOutlines={settings.showOutlines}
            showGrid={settings.showGrid}
            comparing={comparing}
            onSelect={(index) => void navigateTo(index)}
            onAdd={() => fileInputRef.current?.click()}
            onRemove={requestRemove}
            onReorder={store.reorder}
            onZoomChange={setZoom}
            onFit={fit}
            onShowOutlinesChange={(showOutlines) => updateSettings({ showOutlines })}
            onShowGridChange={(showGrid) => updateSettings({ showGrid })}
            onCompareChange={setComparing}
          />
        </div>
      ) : (
        <EmptyState
          loading={loading}
          error={toast}
          onOpenFiles={() => fileInputRef.current?.click()}
          onOpenFolder={() => folderInputRef.current?.click()}
          onLoadDemo={() => void loadDemo()}
        />
      )}

      {toast && activeImage && (
        <div className="toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
      {dropActive && (
        <div className="drop-overlay" aria-hidden="true">
          <div>
            <Icon name="upload" size={40} />
            <strong>ドロップして画像を追加</strong>
          </div>
        </div>
      )}

      <ExportDialog
        open={dialog?.kind === "export"}
        count={images.length}
        width={activeImage?.width ?? 1}
        height={activeImage?.height ?? 1}
        scope={exportScope}
        busy={busy}
        progress={exportProgress}
        format={settings.exportFormat}
        scale={settings.exportScale}
        stripMetadata={settings.stripMetadata}
        quality={settings.exportQuality}
        removeBackground={settings.exportRemoveBackground}
        autoDownload={settings.autoDownload}
        lossy={lossy}
        onScopeChange={setExportScope}
        onFormatChange={(exportFormat) =>
          updateSettings({
            exportFormat,
            exportRemoveBackground: exportFormat === "jpeg" ? false : settings.exportRemoveBackground,
          })
        }
        onScaleChange={(exportScale) => updateSettings({ exportScale })}
        onStripMetadataChange={(stripMetadata) => updateSettings({ stripMetadata })}
        onQualityChange={(exportQuality) => updateSettings({ exportQuality })}
        onRemoveBackgroundChange={(exportRemoveBackground) =>
          updateSettings({
            exportRemoveBackground,
            exportFormat: exportRemoveBackground ? "png" : settings.exportFormat,
          })
        }
        onAutoDownloadChange={(autoDownload) => updateSettings({ autoDownload })}
        onClose={() => {
          if (!busy) setDialog(null);
        }}
        onConfirm={() => void exportImages()}
      />
      <ShortcutsDialog open={dialog?.kind === "shortcuts"} onClose={() => setDialog(null)} />
      <ConfirmDialog
        open={dialog?.kind === "home"}
        title="最初の画面に戻りますか？"
        description="開いている画像と加工内容はすべて閉じられます。ダウンロードしていない加工は失われます。"
        confirmLabel="閉じて戻る"
        onCancel={() => setDialog(null)}
        onConfirm={goHome}
      />
      <ConfirmDialog
        open={dialog?.kind === "discard"}
        title="すべての加工を取り消しますか？"
        description="画像は残したまま、すべての画像のモザイク・ぼかし・塗り・消しゴム・AI復元を取り除きます。この操作は元に戻せません。"
        confirmLabel="すべて取り消す"
        onCancel={() => setDialog(null)}
        onConfirm={discardAll}
      />
      <ConfirmDialog
        open={dialog?.kind === "remove"}
        title="この画像を一覧から外しますか？"
        description={`${
          dialog?.kind === "remove" ? images[dialog.index]?.name : ""
        } にはダウンロードしていない加工があります。`}
        confirmLabel="外す"
        onCancel={() => setDialog(null)}
        onConfirm={() => {
          if (dialog?.kind === "remove") store.remove(dialog.index);
          setDialog(null);
        }}
      />
    </div>
  );
}
